import * as React from "react";
import { OverlayView } from "@react-google-maps/api";
import type { IconProps } from "@ogt/ui";

export type LivePulsingMarkerProps = {
  position: google.maps.LatLngLiteral;
  size?: number;
  color?: string;
  ringCount?: number;
  ringScale?: number;
  label?: string;
  iconName?: IconProps["name"];
  bearingDeg?: number;
  keepTextUpright?: boolean;
  zIndex?: number;
  clickable?: boolean;
  className?: string;
};

export const LivePulsingMarker: React.FC<LivePulsingMarkerProps> = ({
  position,
  size = 40,
  color = "#2D7EF7",
  ringCount = 3,
  ringScale = 0.55,
  label,
  iconName,
  bearingDeg = 0,
  keepTextUpright = true,
  zIndex,
  clickable = false,
  className,
}) => {
  const diameter = size;

  const ringDiameter = Math.max(16, diameter * ringScale);
  const centerDotSize = Math.max(9, diameter * 0.34);
  const labelFontSize = Math.max(8, Math.floor(diameter * 0.15));

  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.FLOAT_PANE}
      getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: diameter,
          height: diameter,
          transform: "translate(-50%, -50%)",
          pointerEvents: clickable ? "auto" : "none",
          display: "grid",
          placeItems: "center",
          zIndex,
        }}
        className={className}
      >
        <style>{`
          @keyframes ogtPulse {
            0% { transform: scale(0.1); opacity: 0.35; }
            70% { transform: scale(1.0); opacity: 0.10; }
            100% { transform: scale(1.05); opacity: 0.0; }
          }
        `}</style>

        {/* pulse */}
        {Array.from({ length: ringCount }).map((_, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              width: ringDiameter,
              height: ringDiameter,
              borderRadius: 9999,
              background: color,
              opacity: 0.2,
              animation: "ogtPulse 1.8s ease-out infinite",
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}

        {/* center-dot */}
        <span
          style={{
            position: "absolute",
            width: centerDotSize,
            height: centerDotSize,
            borderRadius: 9999,
            background: `color-mix(in srgb, ${color} 35%, white)`,
            border: `2px solid color-mix(in srgb, ${color} 75%, black)`,
            boxSizing: "border-box",
          }}
        />

        {/* ikon/label */}
        {(iconName || label) && (
          <span
            style={{ transform: `rotate(${bearingDeg}deg)`, lineHeight: 0 }}
          >
            {iconName && (
              <span
                style={{
                  transform: keepTextUpright ? `rotate(${-bearingDeg}deg)` : "",
                  display: "grid",
                  placeItems: "center",
                  color: "#000",
                }}
              />
            )}

            {label && (
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: `translate(-50%, -50%) ${
                    keepTextUpright ? `rotate(${-bearingDeg}deg)` : ""
                  }`,
                  color: "#000",
                  fontSize: labelFontSize,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  lineHeight: 1,
                }}
              >
                {label}
              </span>
            )}
          </span>
        )}
      </div>
    </OverlayView>
  );
};
